import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from '../../hooks/use-toast';
import { orgApi } from '../phase567Api';
import { useAuth } from '../AuthContext';
import { errorMessage } from '../errors';
import LongTextCell from '@/components/LongTextCell';
import { TableScroller } from '@/components/ui/table-scroller';

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', loan_officer: 'Loan Officer', loan_officer_assistant: 'LO Assistant', processor: 'Processor', business_development: 'Business Development', borrower: 'Borrower', realtor: 'Realtor', party: 'Title / Escrow' };

function cloneMatrix(matrix) {
  const out = {};
  Object.keys(matrix || {}).forEach((role) => { out[role] = { ...matrix[role] }; });
  return out;
}

export default function PermissionsTab({ onDirtyChange }) {
  const { user } = useAuth();
  // Only an owner or admin seat edits the grid (staff.manage no longer does); the server enforces the same.
  const canEdit = !!user?.is_super_admin || ['owner', 'admin'].includes(user?.membership_role);

  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState([]);
  const [roles, setRoles] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [lockedRoles, setLockedRoles] = useState([]);
  const [externalRoles, setExternalRoles] = useState([]);
  const [matrixState, setMatrixState] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Tell the page about unsaved edits (it asks before a tab switch) and warn before the browser leaves.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => { if (onDirtyChange) onDirtyChange(false); }, [onDirtyChange]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orgApi.getPermissions();
      setKeys(data.keys || []);
      setRoles(data.roles || []);
      setDefaults(data.defaults || {});
      setLockedRoles(data.locked_roles || []);
      setExternalRoles(data.external_roles || []);
      setMatrixState(cloneMatrix(data.matrix || {}));
      setDirty(false);
    } catch {
      toast({ title: 'Load failed' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = [];
  keys.forEach((k) => { if (!groups.includes(k.group)) groups.push(k.group); });
  const appliesTo = {};
  keys.forEach((k) => { appliesTo[k.key] = k.applies_to || 'all'; });
  // A staff-only key on an external role (borrower / realtor) is n/a, not off.
  const applicable = (role, key) => !externalRoles.includes(role) || appliesTo[key] === 'all';

  const toggle = (role, key) => {
    if (!canEdit || lockedRoles.includes(role) || !applicable(role, key)) return;
    setMatrixState((prev) => ({
      ...prev,
      [role]: { ...prev[role], [key]: !prev[role]?.[key] },
    }));
    setDirty(true);
  };

  const resetToDefaults = () => {
    setMatrixState(cloneMatrix(defaults));
    setDirty(true);
  };

  const save = async () => {
    const overrides = {};
    roles.forEach((role) => {
      if (lockedRoles.includes(role)) return;
      const roleDefaults = defaults[role] || {};
      const roleState = matrixState[role] || {};
      Object.keys(roleState).forEach((key) => {
        if (applicable(role, key) && roleState[key] !== roleDefaults[key]) {
          overrides[role] = overrides[role] || {};
          overrides[role][key] = roleState[key];
        }
      });
    });
    setSaving(true);
    try {
      const res = await orgApi.setPermissions(overrides);
      setMatrixState(cloneMatrix(res.matrix || {}));
      setDirty(false);
      toast({ title: 'Permissions saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden p-16 flex items-center justify-center text-slate-400">
        <Loader2 className="animate-spin mr-2" size={20} /> Loading...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="permissions-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Owner always has every permission.</p>
        <div className="flex items-center gap-4">
          <button onClick={resetToDefaults} disabled={!canEdit} className="text-xs text-slate-500 underline hover:text-slate-700 disabled:opacity-40 disabled:no-underline" data-testid="perm-reset">
            Reset to defaults
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="perm-save"
            className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
          </button>
        </div>
      </div>

      {groups.map((group) => (
        <TableScroller key={group || 'general'} className="bg-white rounded-2xl border border-slate-100 shadow-sm md:[&_th]:whitespace-normal">
          <div className="px-4 py-3 border-b border-slate-100 text-xs uppercase tracking-widest text-slate-400 font-semibold">
            {group || 'General'}
          </div>
          {/* md+: table-fixed + a shared colgroup so every group's columns line up.
              Below md: auto layout, nowrap role headers, and the card scrolls. */}
          <table className="w-full text-sm table-auto md:table-fixed">
            <colgroup>
              <col className="w-56 md:w-[32%]" />
              {roles.map((role) => <col key={role} />)}
            </colgroup>
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="px-4 py-3 align-bottom">Permission</th>
                {roles.map((role) => (
                  <th key={role} className="px-4 py-3 text-center align-bottom min-w-[5rem]">{ROLE_LABELS[role] || role}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.filter((k) => k.group === group).map((k) => (
                <tr key={k.key} className="border-b border-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-brand-navy">{k.label}</div>
                    {k.description && <LongTextCell id={`perm-desc-${k.key}`} text={k.description} lines={2} maxWidth={320} className="text-xs text-slate-500" />}
                  </td>
                  {roles.map((role) => {
                    const locked = lockedRoles.includes(role);
                    const checked = locked ? true : !!matrixState[role]?.[k.key];
                    if (!applicable(role, k.key)) {
                      return (
                        <td key={role} className="px-4 py-3 text-center text-xs font-semibold tracking-wide text-slate-500"
                          data-testid={`perm-na-${role}-${k.key}`} title="Not applicable to this role">
                          N/A
                        </td>
                      );
                    }
                    return (
                      <td key={role} className="px-4 py-3 text-center">
                        <div className="inline-flex items-center gap-1">
                          <Checkbox
                            checked={checked}
                            disabled={!canEdit || locked}
                            onCheckedChange={() => toggle(role, k.key)}
                            data-testid={`perm-cell-${role}-${k.key}`}
                          />
                          {locked && <Lock size={10} className="text-slate-300" title="Owner always has every permission" />}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      ))}
    </div>
  );
}
